import { FC } from 'react'
import moment from 'moment'
import { useNavigate } from 'react-router-dom'
import { DashboardStatsProps } from './types'
import {
  ArrowUpRightIcon,
  InboxArrowDownIcon,
  UserGroupIcon,
  MinusIcon,
} from '@heroicons/react/24/outline'

export const StatsGlassGrid: FC<DashboardStatsProps> = ({ groups }) => {
  const navigate = useNavigate()

  const goToConsult = (
    range: 'today' | 'last7' | 'last30' | 'year',
    isDelivered: boolean,
    isVoided: boolean,
    origin: 'original' | 'main',
    shipmentFilterType?: number
  ) => {
    const today = moment()
    const from =
      origin === 'main'
        ? moment(today).startOf('month').format('YYYY-MM-DD')
        : range === 'today'
          ? moment(today).format('YYYY-MM-DD')
          : range === 'last7'
            ? moment(today).subtract(7, 'days').format('YYYY-MM-DD')
            : range === 'last30'
              ? moment(today).subtract(30, 'days').format('YYYY-MM-DD')
              : moment(today).startOf('year').format('YYYY-MM-DD')
    const to = moment(today).format('YYYY-MM-DD')

    const params = new URLSearchParams()

    // Only add dates if shipmentFilterType is NOT 1 or 3
    if (shipmentFilterType !== 1 && shipmentFilterType !== 3) {
      params.set('from', from)
      params.set('to', to)
      params.set('dateField', 'creationDate')
    }

    params.set('origin', origin)
    if (isDelivered) params.set('status', 'DELIVERED')
    if (isVoided) params.set('status', 'ANNULLED')
    if (shipmentFilterType)
      params.set('shipmentFilterType', shipmentFilterType.toString())
    navigate(`/shipments/consult?${params.toString()}`)
  }

  const fallbackIcons = [InboxArrowDownIcon, UserGroupIcon]

  return (
    <div className="grid grid-cols-1 xl:grid-cols-2 gap-9">
      {groups.map((g, idx) => {
        const Icon =
          (g.icon as any) || fallbackIcons[idx % fallbackIcons.length]
        const accent = 'bg-main-500'
        const isDelivered = g.title.toLowerCase().includes('entregadas')
        const isVoided = g.title.toLowerCase().includes('anuladas')
        const isInvoiced = g.title.toLowerCase().includes('facturadas')
        const origin = g.consultOrigin ?? 'main'
        const isInteractive = !g.title
          .toLowerCase()
          .includes('guías en tránsito')

        return (
          <article
            key={g.title}
            className={`group/article relative overflow-hidden rounded-xl border border-main-100/60 bg-white/75 backdrop-blur shadow-sm transition hover:shadow-md ${
              isInteractive
                ? 'hover:-translate-y-1 cursor-pointer'
                : 'cursor-default'
            }`}
            onClick={
              origin === 'main' && isInteractive
                ? () =>
                    goToConsult(
                      'today',
                      isDelivered,
                      isVoided,
                      origin,
                      isInvoiced ? 2 : undefined
                    )
                : undefined
            }
          >
            <div className="absolute inset-x-0 top-0 " />
            <div className="absolute -right-10 -top-10 h-32 w-32 rounded-full bg-main-500/10 blur-3xl" />

            <header className="relative flex items-center gap-4 px-5 pt-5 pb-4">
              <div
                className={`${accent} rounded-xl p-3 shadow-inner shadow-main-900/10`}
              >
                <Icon className="h-6 w-6 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs uppercase tracking-wide text-gray-500">
                  Resumen
                </p>
                <h3 className="text-lg font-semibold text-gray-900 truncate">
                  {g.title}
                </h3>
              </div>
              {origin === 'main' && (
                <div className="ml-2 flex items-center justify-end">
                  <ArrowUpRightIcon className="h-4 w-4 text-main-500" />
                </div>
              )}
            </header>

            <section className="relative px-5 pb-5 grid grid-cols-2 sm:grid-cols-4 gap-3">
              {[
                { label: 'Hoy', value: g.today, range: 'today' as const },
                {
                  label: 'Últimos 7 días',
                  value: g.last7,
                  range: 'last7' as const,
                },
                {
                  label: 'Últimos 30 días',
                  value: g.last30,
                  range: 'last30' as const,
                },
                {
                  label: 'Año actual',
                  value: g.currentYear ?? 0,
                  range: 'year' as const,
                },
              ].map((m) => (
                <div
                  key={`${g.title}-${m.label}`}
                  className={`group/card relative rounded-lg border-b-2 border-b-main-200 bg-gray-100 backdrop-blur-sm p-3 shadow-xs hover:border-b-main-500 hover:bg-main-50/60 hover:shadow-sm transition ${
                    isInteractive ? 'cursor-pointer' : 'cursor-default'
                  }`}
                  onClick={
                    origin === 'original' && isInteractive
                      ? () =>
                          goToConsult(m.range, isDelivered, isVoided, origin)
                      : undefined
                  }
                >
                  <div className="flex items-center justify-between text-xs text-gray-500">
                    <span>{m.label}</span>
                  </div>
                  <div className="mt-1 flex items-center gap-3">
                    <span className="text-3xl font-semibold text-main-400 tracking-tight">
                      {m.value}
                    </span>
                    {(() => {
                      const value = m.value
                      if (value === 0) {
                        return (
                          <div className="flex-1 flex items-center justify-center h-12 pb-1">
                            <MinusIcon className="h-6 w-6 mt-2 text-gray-400" />
                          </div>
                        )
                      }
                      if (value < 0) return null

                      let imageSrc = ''
                      if (value >= 1 && value <= 10)
                        imageSrc = '/CAJAS/1-10.png'
                      else if (value >= 11 && value <= 100)
                        imageSrc = '/CAJAS/11-100.png'
                      else if (value >= 101) imageSrc = '/CAJAS/101.png'
                      else return null

                      return (
                        <div className="flex-1 flex items-end justify-end h-12 pb-1">
                          <img
                            src={imageSrc}
                            alt="Rango"
                            className="h-full w-auto object-contain"
                          />
                        </div>
                      )
                    })()}
                  </div>
                  <div className="mt-2 h-1.5 w-full rounded-full bg-gradient-to-r from-main-200 via-main-400 to-main-600 opacity-30 group-hover/card:opacity-80 transition-opacity" />
                  {origin === 'original' && (
                    <button
                      type="button"
                      aria-label={`Ir a ${m.label}`}
                      className="absolute top-2 right-2 rounded-md border border-transparent text-main-500 opacity-0 pointer-events-none group-hover/card:opacity-100 group-hover/card:pointer-events-auto group-hover/card:bg-white/70 transition"
                      onClick={(e) => {
                        e.stopPropagation()
                        goToConsult(m.range, isDelivered, isVoided, origin)
                      }}
                    >
                      <ArrowUpRightIcon className="h-4 w-4" />
                    </button>
                  )}
                </div>
              ))}
            </section>
          </article>
        )
      })}
    </div>
  )
}

export default StatsGlassGrid
